import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  CommitList as FrmCommitList,
  EmptyState,
  ErrorBanner,
  FrmIcon,
  Markdown,
  MetricStrip,
  Modal,
  PageHeader,
  Pill,
  ProgressBar,
  RoadmapTaskList,
  Skeleton,
  StatusPill,
  Surface,
  Tabs,
  WorkbenchPanel,
  SprintList as FrmSprintList,
  useToast,
} from '@frm/ui'

import { createChatSession } from '../api/chat'
import {
  fetchProject,
  fetchProjectContext,
  fetchProjectContextPreview,
  type ProjectConceptBlock,
  type ProjectCommit,
  type ProjectContextPreviewDto,
  type ProjectDto,
  type ProjectSprint,
  type ProjectTask,
  type ProjectTaskState,
} from '../api/projects'
import { fetchSessions, type OwnerSessionDto, type SessionStatus } from '../api/sessions'
import { formatAge, formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'
import { InProgressAction } from '../shared/in-progress'

const PAGE_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minWidth: 0,
}

const STACK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}

const GRID_STYLE: CSSProperties = {
  alignItems: 'start',
  display: 'grid',
  gap: 18,
  gridTemplateColumns: 'minmax(0, 1.45fr) minmax(340px, 1fr)',
}

const SPRINT_TASK_GRID_STYLE: CSSProperties = {
  alignItems: 'stretch',
  display: 'grid',
  gap: 18,
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
}

const FILL_PANEL_STYLE: CSSProperties = {
  height: '100%',
}

const PANEL_TABS_STYLE: CSSProperties = {
  marginBottom: 14,
  maxWidth: '100%',
  overflowX: 'auto',
  paddingBottom: 2,
}

const META_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gap: 11,
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted)',
}

const PROJECT_GRADIENTS = [
  ['#38BDF8', '#6366F1'],
  ['#6366F1', '#8B5CF6'],
  ['#34D399', '#0EA5E9'],
  ['#0EA5E9', '#0369A1'],
  ['#FBBF24', '#F59E0B'],
  ['#64748B', '#475569'],
]

type SprintFilter = 'all' | 'active' | 'planned' | 'closed'
type TaskFilter = 'all' | 'work' | 'done'

interface SprintListItem extends ProjectSprint {
  tasks?: ProjectTask[]
}

function projectGradient(project: ProjectDto): string {
  const hash = project.id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const pair = PROJECT_GRADIENTS[hash % PROJECT_GRADIENTS.length]
  const from = pair?.[0] ?? '#38BDF8'
  const to = pair?.[1] ?? '#6366F1'
  return `linear-gradient(135deg, ${from}, ${to})`
}

function projectAccent(project: ProjectDto): string {
  return projectGradient(project).match(/#(?:[0-9a-f]{6})/i)?.[0] ?? '#38BDF8'
}

function statusKind(status: ProjectDto['status']) {
  if (status === 'active') return 'ok' as const
  if (status === 'planned') return 'warning' as const
  return 'synced' as const
}

function normalizedSprintState(state: string): SprintFilter {
  if (state === 'closed') return 'closed'
  if (state === 'planned') return 'planned'
  return 'active'
}

function sprintStatusKind(state: string) {
  const normalized = normalizedSprintState(state)
  if (normalized === 'active') return 'ok' as const
  if (normalized === 'planned') return 'warning' as const
  return 'synced' as const
}

function sprintShortId(value: string): string {
  const normalized = value.replace(/^\/+|\/+$/g, '').replace(/^sprints\//, '')
  const parts = normalized.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? normalized
}

function projectSprintItems(project: ProjectDto): SprintListItem[] {
  const history = project.sprintHistory.length > 0 ? project.sprintHistory : project.sprint ? [project.sprint] : []
  return history.map((sprint) => {
    if (sprint.tasks?.length) return sprint
    if (project.sprint?.id === sprint.id) return { ...sprint, tasks: project.sprint.tasks ?? project.tasks }
    return sprint
  })
}

function sprintTasks(project: ProjectDto, sprint: SprintListItem | null): ProjectTask[] {
  if (!sprint) return []
  if (sprint.tasks?.length) return sprint.tasks
  if (project.sprint?.id === sprint.id) return project.sprint.tasks ?? project.tasks
  return []
}

function taskMatchesFilter(task: ProjectTask, filter: TaskFilter): boolean {
  if (filter === 'done') return task.st === 'x'
  if (filter === 'work') return task.st !== 'x'
  return true
}

function normalizeProjectId(value: string | null | undefined): string {
  return (value ?? '').replace(/^\/+|\/+$/g, '')
}

function isProjectOrChild(parentId: string, candidateId: string | null | undefined): boolean {
  const parent = normalizeProjectId(parentId)
  const candidate = normalizeProjectId(candidateId)
  return Boolean(parent && candidate && (candidate === parent || candidate.startsWith(`${parent}/`)))
}

function isActiveSession(session: OwnerSessionDto): boolean {
  return session.status === 'running' || session.status === 'idle'
}

function sessionStatusTone(status: SessionStatus): 'ok' | 'warning' | 'danger' | 'unknown' {
  if (status === 'running') return 'ok'
  if (status === 'idle') return 'warning'
  if (status === 'error') return 'danger'
  return 'unknown'
}

function sessionSortWeight(session: OwnerSessionDto): number {
  const statusWeight = session.status === 'running' ? 10_000_000_000_000 : 0
  const activity = Date.parse(session.lastActivityAt)
  return statusWeight + (Number.isFinite(activity) ? activity : 0)
}

function commitMatchesSprint(commit: ProjectCommit, sprint: SprintListItem | null): boolean {
  if (!sprint || !commit.sprint) return false
  return commit.sprint === sprint.id || sprintShortId(commit.sprint) === sprintShortId(sprint.id)
}

function formatUpdated(value: string): string {
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return value || 'unknown'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(ts))
}

function ageLabel(hours: number): string {
  if (hours < 24) return `${hours} ч`
  return `${Math.round(hours / 24)} дн`
}

function formatTokens(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace('.0', '')}k`
  return formatInteger(value)
}

function Tile({ project, size = 54 }: { project: ProjectDto; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        alignItems: 'center',
        background: projectGradient(project),
        borderRadius: Math.round(size / 4),
        boxShadow: '0 10px 26px rgba(56, 189, 248, 0.18)',
        color: '#04111d',
        display: 'inline-flex',
        flexShrink: 0,
        fontSize: size > 44 ? 18 : 13,
        fontWeight: 800,
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      {project.abbr}
    </span>
  )
}

function MetaCard({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'info' | 'success' | 'warning' }) {
  const color = tone === 'info' ? 'var(--frm-accent)' : tone === 'success' ? 'var(--frm-success)' : tone === 'warning' ? 'var(--frm-warning)' : 'var(--frm-text-secondary)'
  return (
    <Surface padding="md" variant="subtle">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: 'var(--frm-text-muted)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          {label}
        </span>
        <span style={{ ...MONO_NUMERIC_STYLE, color, fontSize: 13.5, fontWeight: 700 }}>{value}</span>
      </div>
    </Surface>
  )
}

function ConceptBlocks({ blocks }: { blocks: ProjectConceptBlock[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {blocks.map((block, index) => {
        if (block.kind === 'h') {
          return (
            <span key={`${block.kind}-${index}`} style={{ color: 'var(--frm-accent)', fontSize: 11, fontWeight: 800, letterSpacing: '.08em', marginTop: 4, textTransform: 'uppercase' }}>
              {block.text}
            </span>
          )
        }
        if (block.kind === 'li') {
          return (
            <p key={`${block.kind}-${index}`} style={{ color: 'var(--frm-text-secondary)', fontSize: 13.5, lineHeight: 1.55, margin: 0 }}>
              <span style={{ color: 'var(--frm-accent)', marginRight: 8 }}>•</span>
              {block.text}
            </p>
          )
        }
        return (
          <p key={`${block.kind}-${index}`} style={{ color: 'var(--frm-text-secondary)', fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            {block.text}
          </p>
        )
      })}
    </div>
  )
}

function ContextAssembly({ project, preview }: { project: ProjectDto; preview?: ProjectContextPreviewDto }) {
  const layers = useMemo(() => {
    const sourceLayers = preview?.layers?.length
      ? preview.layers
      : [
          { id: 'skills', label: 'Скилы', source: 'platform + project skills', estimatedTokens: 1200 },
          { id: 'project', label: 'Проект', source: `${project.id}/context.md`, estimatedTokens: Math.max(200, project.desc.length * 2) },
          { id: 'sprint', label: 'Спринт', source: project.sprint?.id ?? 'none', estimatedTokens: Math.max(0, project.tasks.length * 140) },
          { id: 'session', label: 'Сессия', source: 'runtime conversation', estimatedTokens: 0 },
          { id: 'request', label: 'Запрос', source: 'current user message', estimatedTokens: 0 },
        ]
    return sourceLayers
  }, [preview, project])
  const [visibleCount, setVisibleCount] = useState(layers.length)
  const [playing, setPlaying] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (!playing) setVisibleCount(layers.length)
  }, [layers.length, playing])

  useEffect(() => () => {
    if (timer.current !== null) window.clearInterval(timer.current)
  }, [])

  const totalTokens = layers.reduce((sum, layer) => sum + layer.estimatedTokens, 0)
  const windowMax = 32_000

  const play = () => {
    if (timer.current !== null) window.clearInterval(timer.current)
    setPlaying(true)
    setVisibleCount(0)
    timer.current = window.setInterval(() => {
      setVisibleCount((current) => {
        const next = current + 1
        if (next >= layers.length) {
          if (timer.current !== null) window.clearInterval(timer.current)
          timer.current = null
          setPlaying(false)
          return layers.length
        }
        return next
      })
    }, 520)
  }

  return (
    <WorkbenchPanel
      title="Формирование контекста запроса"
      description="что агент подтягивает в контекст перед ответом — слой за слоем"
      actions={
        <Button variant="secondary" size="sm" leftIcon={<PlayIcon />} onClick={play} loading={playing}>
          {playing ? 'Сборка...' : 'Проиграть'}
        </Button>
      }
    >
      <div className="projects-context-grid" style={{ display: 'grid', gap: 18, gridTemplateColumns: 'minmax(0, 1.55fr) minmax(280px, 1fr)', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {layers.map((layer, index) => {
            const revealed = index < visibleCount
            const accent = ['#818CF8', '#38BDF8', '#7DDBFF', '#34D399', '#FBBF24'][index % 5] ?? '#38BDF8'
            return (
              <div
                key={layer.id}
                style={{
                  background: layer.id === 'request' && revealed ? 'rgba(251, 191, 36, 0.07)' : 'var(--frm-workbench-well)',
                  border: `1px solid ${revealed ? `${accent}55` : 'var(--frm-line-faint)'}`,
                  borderLeft: `3px solid ${revealed ? accent : 'var(--frm-line-subtle)'}`,
                  borderRadius: 13,
                  display: 'flex',
                  gap: 13,
                  opacity: revealed ? 1 : 0.42,
                  padding: '13px 15px',
                  transform: revealed ? 'translateY(0)' : 'translateY(5px)',
                  transition: 'all 320ms cubic-bezier(.2,0,0,1)',
                }}
              >
                <span style={{ ...MONO_NUMERIC_STYLE, alignItems: 'center', background: `${accent}1f`, border: `1px solid ${accent}55`, borderRadius: 9, color: accent, display: 'inline-flex', flexShrink: 0, fontSize: 12, fontWeight: 700, height: 30, justifyContent: 'center', width: 30 }}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 5, minWidth: 0 }}>
                  <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 9 }}>
                    <span style={{ color: revealed ? 'var(--frm-text-primary)' : 'var(--frm-text-muted)', fontSize: 13.5, fontWeight: 800 }}>{layer.label}</span>
                    <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 10.5 }}>{layer.id}</span>
                    <span style={{ flex: 1 }} />
                    {revealed ? <FrmIcon name="check" size={14} style={{ color: accent }} /> : null}
                  </div>
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 10.5, opacity: 0.78, wordBreak: 'break-word' }}>{layer.source}</span>
                </div>
                <Pill tone="neutral" size="sm">≈ {formatTokens(layer.estimatedTokens)} ток.</Pill>
              </div>
            )
          })}
        </div>
        <Surface padding="md" variant="subtle">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ color: 'var(--frm-text-muted)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>Контекст-окно запроса</span>
              <div style={{ alignItems: 'baseline', display: 'flex', gap: 8 }}>
                <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 30, fontWeight: 700, lineHeight: 1 }}>{formatTokens(totalTokens)}</span>
                <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 13 }}>/ 32k бюджет</span>
              </div>
            </div>
            <div style={{ background: 'var(--frm-workbench-well-2)', border: '1px solid var(--frm-line-faint)', borderRadius: 999, display: 'flex', height: 12, overflow: 'hidden' }}>
              {layers.map((layer, index) => (
                <div
                  key={layer.id}
                  style={{
                    background: ['#818CF8', '#38BDF8', '#7DDBFF', '#34D399', '#FBBF24'][index % 5],
                    height: '100%',
                    transition: 'width 420ms cubic-bezier(.2,0,0,1)',
                    width: index < visibleCount ? `${Math.max(1, (layer.estimatedTokens / windowMax) * 100)}%` : 0,
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {layers.map((layer, index) => (
                <div key={layer.id} style={{ alignItems: 'center', display: 'flex', gap: 9 }}>
                  <span style={{ background: ['#818CF8', '#38BDF8', '#7DDBFF', '#34D399', '#FBBF24'][index % 5], borderRadius: 2, flexShrink: 0, height: 8, width: 8 }} />
                  <span style={{ color: 'var(--frm-text-muted)', flex: 1, fontSize: 12 }}>{layer.label}</span>
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11 }}>{formatTokens(layer.estimatedTokens)}</span>
                </div>
              ))}
            </div>
            <div style={{ alignItems: 'center', background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.34)', borderRadius: 10, color: 'var(--frm-success)', display: 'flex', fontSize: 12.5, fontWeight: 800, gap: 9, padding: '11px 14px' }}>
              <FrmIcon name="send" size={14} />
              контекст готов · агент {project.chatAgent}
            </div>
          </div>
        </Surface>
      </div>
    </WorkbenchPanel>
  )
}

function CommitQueue({ project }: { project: ProjectDto }) {
  return (
    <WorkbenchPanel
      title="Не выпущенные коммиты"
      description="git log @{u}..HEAD --oneline"
      actions={<Pill tone={project.commits.length > 0 ? 'info' : 'success'}>{project.commits.length > 0 ? `ahead +${project.commits.length}` : 'up-to-date'}</Pill>}
    >
      <FrmCommitList
        variant="queue"
        commits={project.commits.map((commit) => ({
          hash: commit.hash,
          subject: commit.subj,
          author: commit.author,
          sprint: commit.sprint,
          ageLabel: ageLabel(commit.ageHours),
          unreleased: true,
          hotfix: commit.hotfix,
        }))}
        emptyText="Прод совпадает с origin — выпускать нечего."
        actions={() => (
          <>
            <InProgressAction
              id="projects.commit.defer"
              render={(props) => (
                <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} size="sm" variant="ghost">
                  Отложить
                </Button>
              )}
            />
            <InProgressAction
              id="projects.commit.release"
              render={(props) => (
                <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} size="sm" variant="primary" leftIcon={<FrmIcon name="upload" size={13} />}>
                  Выпустить
                </Button>
              )}
            />
          </>
        )}
      />
    </WorkbenchPanel>
  )
}

function SprintList({
  sprints,
  selectedSprintId,
  onSelect,
}: {
  sprints: SprintListItem[]
  selectedSprintId: string | null
  onSelect: (id: string) => void
}) {
  const [filter, setFilter] = useState<SprintFilter>('all')
  const visible = filter === 'all' ? sprints : sprints.filter((sprint) => normalizedSprintState(sprint.state) === filter)
  const counts: Record<SprintFilter, number> = {
    all: sprints.length,
    active: sprints.filter((sprint) => normalizedSprintState(sprint.state) === 'active').length,
    planned: sprints.filter((sprint) => normalizedSprintState(sprint.state) === 'planned').length,
    closed: sprints.filter((sprint) => normalizedSprintState(sprint.state) === 'closed').length,
  }

  return (
    <WorkbenchPanel
      title="Спринты проекта"
      description="из roadmap.md · сгруппированы по статусу"
      style={FILL_PANEL_STYLE}
    >
      <div style={PANEL_TABS_STYLE}>
        <Tabs
          value={filter}
          onChange={(id) => setFilter(id as SprintFilter)}
          ariaLabel="Фильтр спринтов проекта"
          items={[
            { id: 'all', label: <>Все <span style={MONO_NUMERIC_STYLE}>{counts.all}</span></> },
            { id: 'active', label: <>Активные <span style={MONO_NUMERIC_STYLE}>{counts.active}</span></> },
            { id: 'planned', label: <>Планируемые <span style={MONO_NUMERIC_STYLE}>{counts.planned}</span></> },
            { id: 'closed', label: <>Завершённые <span style={MONO_NUMERIC_STYLE}>{counts.closed}</span></> },
          ]}
        />
      </div>
      <FrmSprintList
        items={visible.map((sprint) => ({
          id: sprint.id,
          state: normalizedSprintState(sprint.state),
          goal: sprint.goal || 'Цель спринта не указана',
          done: sprint.done,
          total: sprint.total,
          tasks: sprint.tasks?.map((task, index) => ({
            id: `${sprint.id}-${index}`,
            state: task.st,
            text: task.text,
            criteria: task.criteria,
          })),
          badge: (sprint.tasks?.length ?? 0) > 0 ? <Pill tone="neutral" size="sm">roadmap tasks</Pill> : undefined,
        }))}
        selectedId={selectedSprintId}
        onItemClick={onSelect}
        emptyText="По текущему фильтру нет записей sprintHistory."
        groupLabels={{ active: 'Активные', planned: 'Планируемые', closed: 'Завершённые' }}
      />
    </WorkbenchPanel>
  )
}

function ActiveSprintCard({ project }: { project: ProjectDto }) {
  const sprint = project.sprint
  if (!sprint) {
    return (
      <WorkbenchPanel title="Активный спринт">
        <EmptyState title="Активного спринта нет" description="Проект в планировании или закрыт." />
      </WorkbenchPanel>
    )
  }
  const pct = sprint.total > 0 ? Math.round((sprint.done / sprint.total) * 100) : 0
  return (
    <WorkbenchPanel title="Активный спринт">
      <div style={STACK_STYLE}>
        <Surface padding="md" variant="subtle" style={{ borderColor: 'rgba(56, 189, 248, 0.2)' }}>
          <div style={STACK_STYLE}>
            <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 9 }}>
              <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 13, fontWeight: 700 }}>{sprint.id}</span>
              <StatusPill status="ok" label={normalizedSprintState(sprint.state)} />
            </div>
            <span style={{ color: 'var(--frm-text-secondary)', fontSize: 13.5, lineHeight: 1.45 }}>{sprint.goal}</span>
            <div style={{ alignItems: 'center', display: 'flex', gap: 11 }}>
              <ProgressBar
                value={sprint.done}
                max={sprint.total}
                tone="accent"
                size="md"
                style={{ flex: 1 }}
                ariaLabel={`Прогресс спринта ${sprint.id}`}
              />
              <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-secondary)', fontSize: 12, fontWeight: 700 }}>{sprint.done}/{sprint.total}</span>
            </div>
          </div>
        </Surface>
        <Button size="sm" variant="secondary" leftIcon={<ListIcon />} disabled>
          Полный roadmap спринта
        </Button>
      </div>
    </WorkbenchPanel>
  )
}

function SprintTasks({
  project,
  sprint,
  openingChat,
  onOpenChat,
}: {
  project: ProjectDto
  sprint: SprintListItem | null
  openingChat: boolean
  onOpenChat: (input?: { agent?: string; sprint?: string | null }) => void
}) {
  const [filter, setFilter] = useState<TaskFilter>('work')
  const tasks = sprintTasks(project, sprint)
  const visibleTasks = tasks.filter((task) => taskMatchesFilter(task, filter))
  const deliveries = project.commits.filter((commit) => commitMatchesSprint(commit, sprint))
  const counts: Record<TaskFilter, number> = {
    all: tasks.length,
    work: tasks.filter((task) => task.st !== 'x').length,
    done: tasks.filter((task) => task.st === 'x').length,
  }

  return (
    <WorkbenchPanel
      title={sprint ? `Задачи · ${sprint.id}` : 'Задачи спринта'}
      description="roadmap.md выбранного спринта"
      style={FILL_PANEL_STYLE}
    >
      <div style={PANEL_TABS_STYLE}>
        <Tabs
          value={filter}
          onChange={(id) => setFilter(id as TaskFilter)}
          ariaLabel="Фильтр задач выбранного спринта"
          items={[
            { id: 'all', label: <>Все <span style={MONO_NUMERIC_STYLE}>{counts.all}</span></> },
            { id: 'work', label: <>В работе <span style={MONO_NUMERIC_STYLE}>{counts.work}</span></> },
            { id: 'done', label: <>Завершённые <span style={MONO_NUMERIC_STYLE}>{counts.done}</span></> },
          ]}
        />
      </div>
      {!sprint ? (
        <EmptyState title="Спринт не выбран" description="Выберите спринт слева, чтобы увидеть его задачи." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <Surface padding="md" variant="subtle">
            <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <StatusPill status={sprintStatusKind(sprint.state)} label={normalizedSprintState(sprint.state)} />
              <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 12.5 }}>{sprintShortId(sprint.id)}</span>
              <span style={{ color: 'var(--frm-text-muted)', flex: 1, fontSize: 12 }}>{sprint.goal || 'Цель спринта не указана'}</span>
              <Pill tone="neutral" size="sm">{sprint.done}/{sprint.total}</Pill>
            </div>
          </Surface>

          {visibleTasks.length === 0 ? (
            <EmptyState
              title={tasks.length === 0 ? 'Задачи не найдены' : 'Нет задач по фильтру'}
              description={tasks.length === 0 ? 'В API нет задач для выбранного roadmap.md.' : 'Переключите фильтр задач выбранного спринта.'}
            />
          ) : (
            <RoadmapTaskList
              tasks={visibleTasks.map((task, index) => ({
                id: `${task.st}-${index}-${task.text}`,
                state: task.st,
                text: task.text,
                criteria: task.criteria,
              }))}
              actions={(task) => task.state !== 'x' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<ChatIcon />}
                  loading={openingChat}
                  onClick={() => onOpenChat({ sprint: sprint?.id ?? project.sprint?.id ?? null })}
                >
                  Открыть в чате с агентом
                </Button>
              ) : null}
            />
          )}

          <WorkbenchPanel
            title="Сдачи"
            description="невыпущенные коммиты выбранного спринта"
            actions={<Pill tone={deliveries.length > 0 ? 'info' : 'neutral'}>{deliveries.length}</Pill>}
          >
            {deliveries.length === 0 ? (
              <Surface padding="md" variant="subtle">
                <span style={MUTED_STYLE}>Невыпущенных сдач по этому спринту нет.</span>
              </Surface>
            ) : (
              <FrmCommitList
                variant="history"
                density="compact"
                showUnreleased={false}
                commits={deliveries.map((commit) => ({
                  hash: commit.hash,
                  subject: commit.subj,
                  author: commit.author,
                  sprint: commit.sprint,
                  ageLabel: ageLabel(commit.ageHours),
                  hotfix: commit.hotfix,
                }))}
              />
            )}
          </WorkbenchPanel>
        </div>
      )}
    </WorkbenchPanel>
  )
}

function ProjectSessionsWidget({
  project,
  openingChat,
  onOpenChat,
}: {
  project: ProjectDto
  openingChat: boolean
  onOpenChat: (input?: { agent?: string; sprint?: string | null }) => void
}) {
  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'project', project.id],
    queryFn: fetchSessions,
    refetchInterval: 30_000,
  })
  const sessions = useMemo(
    () => (sessionsQuery.data ?? [])
      .filter((session) => isActiveSession(session) && isProjectOrChild(project.id, session.project))
      .sort((a, b) => sessionSortWeight(b) - sessionSortWeight(a)),
    [project.id, sessionsQuery.data],
  )

  return (
    <WorkbenchPanel
      title="Активные сессии проекта"
      description={sessionsQuery.data ? `${sessions.length} running/idle по проекту и подпроектам` : 'live /api/sessions'}
      actions={<Button size="sm" variant="ghost" loading={sessionsQuery.isFetching} onClick={() => void sessionsQuery.refetch()}>Обновить</Button>}
    >
      {sessionsQuery.isLoading ? (
        <Skeleton height={140} />
      ) : sessionsQuery.isError ? (
        <ErrorBanner
          title="Сессии недоступны"
          description={sessionsQuery.error instanceof Error ? sessionsQuery.error.message : 'unknown error'}
        />
      ) : sessions.length === 0 ? (
        <EmptyState
          title="Активных сессий проекта нет"
          description="Можно открыть новую web-сессию с агентом проекта."
          action={
            <Button size="sm" variant="secondary" leftIcon={<ChatIcon />} loading={openingChat} onClick={() => onOpenChat()}>
              Открыть проект в чате
            </Button>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sessions.map((session) => (
            <Surface key={session.id} padding="md" variant="subtle">
              <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
                <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <StatusPill status={sessionStatusTone(session.status)} label={session.status} />
                  <Pill tone={session.channel === 'web' ? 'info' : 'neutral'}>{session.channel}</Pill>
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 12, fontWeight: 700 }}>{session.agent}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11.5 }}>{formatAge(session.lastActivityAt)}</span>
                </div>
                <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-secondary)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.project}
                  </span>
                  <span style={{ color: 'var(--frm-text-muted)', fontSize: 12 }}>
                    {session.sprint || 'без спринта'} · {session.initiator}
                  </span>
                  {session.task ? (
                    <span style={{ color: 'var(--frm-text-secondary)', fontSize: 12.5, lineHeight: 1.45 }}>
                      {session.task}
                    </span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<ChatIcon />}
                    loading={openingChat}
                    onClick={() => onOpenChat({ agent: session.agent, sprint: session.sprint })}
                  >
                    В чат
                  </Button>
                </div>
              </div>
            </Surface>
          ))}
        </div>
      )}
    </WorkbenchPanel>
  )
}

function ProjectSkeleton() {
  return (
    <div style={STACK_STYLE}>
      <Skeleton height={160} />
      <Skeleton height={250} />
      <Skeleton height={420} />
    </div>
  )
}

export function ProjectDetailPage() {
  const navigate = useNavigate()
  const params = useParams()
  const { toast } = useToast()
  const projectId = decodeURIComponent(params['*'] ?? params.id ?? '')
  const [contextOpen, setContextOpen] = useState(false)
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null)

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId),
    enabled: projectId.length > 0,
  })
  const contextQuery = useQuery({
    queryKey: ['project', projectId, 'context'],
    queryFn: () => fetchProjectContext(projectId),
    enabled: projectId.length > 0,
  })
  const previewQuery = useQuery({
    queryKey: ['project', projectId, 'context-preview'],
    queryFn: () => fetchProjectContextPreview(projectId),
    enabled: projectId.length > 0,
  })

  const project = projectQuery.data
  const sprintItems = useMemo(() => project ? projectSprintItems(project) : [], [project])
  const selectedSprint = useMemo(
    () => sprintItems.find((sprint) => sprint.id === selectedSprintId) ?? sprintItems[0] ?? null,
    [selectedSprintId, sprintItems],
  )
  const openChatMutation = useMutation({
    mutationFn: (input?: { agent?: string; sprint?: string | null }) => {
      const currentProject = projectQuery.data
      if (!currentProject) throw new Error('Проект ещё не загружен')
      return createChatSession({
        agent: input?.agent || currentProject.chatAgent,
        project: currentProject.id,
        sprint: input?.sprint ?? selectedSprint?.id ?? currentProject.sprint?.id ?? null,
        mode: 'chat',
      })
    },
    onSuccess: (session) => {
      navigate(`/chat/${encodeURIComponent(session.agent)}`)
    },
    onError: (error) => toast({
      title: 'Чат не открыт',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })

  useEffect(() => {
    if (!project) return
    const fallbackSprintId = project.sprint?.id ?? sprintItems[0]?.id ?? null
    setSelectedSprintId((current) => {
      if (current && sprintItems.some((sprint) => sprint.id === current)) return current
      return fallbackSprintId
    })
  }, [project, sprintItems])

  if (!projectId) {
    return <EmptyState title="Проект не выбран" description="Вернись к списку проектов и открой карточку." action={<Link to="/projects">Все проекты</Link>} />
  }

  if (projectQuery.isLoading) return <ProjectSkeleton />

  if (projectQuery.isError || !project) {
    return (
      <ErrorBanner
        title="Не удалось загрузить проект"
        description={projectQuery.error instanceof Error ? projectQuery.error.message : 'unknown error'}
        action={<Button onClick={() => void projectQuery.refetch()}>Повторить</Button>}
      />
    )
  }

  const aheadTotal = project.ahead.vault + project.ahead.platform
  const openTasks = project.tasks.filter((task) => task.st !== 'x').length
  const concept = contextQuery.data?.concept ?? project.concept

  return (
    <>
      <div style={PAGE_STYLE}>
        <div style={{ alignItems: 'center', display: 'flex', gap: 9 }}>
          <Link
            to="/projects"
            style={{
              alignItems: 'center',
              background: 'var(--frm-workbench-panel)',
              border: '1px solid var(--frm-line-subtle)',
              borderRadius: 9,
              color: 'var(--frm-text-secondary)',
              display: 'inline-flex',
              fontSize: 12.5,
              fontWeight: 700,
              gap: 7,
              height: 32,
              padding: '0 12px 0 9px',
              textDecoration: 'none',
            }}
          >
            <ChevronIcon back />
            Проекты
          </Link>
          <span style={{ color: 'var(--frm-line-subtle)' }}>/</span>
          <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 12.5 }}>{project.name}</span>
        </div>

        <WorkbenchPanel>
          <div style={{ alignItems: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Tile project={project} />
            <div style={{ display: 'flex', flex: '1 1 260px', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 11 }}>
                <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-primary)', fontSize: 20, fontWeight: 700 }}>{project.name}</span>
                <StatusPill status={statusKind(project.status)} label={project.status} />
                {aheadTotal > 0 || project.commits.length > 0 ? <Pill tone="info">{aheadTotal > 0 ? `vault +${project.ahead.vault} · платформа +${project.ahead.platform}` : `ahead +${project.commits.length}`}</Pill> : null}
              </div>
              <span style={{ color: 'var(--frm-text-secondary)', fontSize: 14, lineHeight: 1.5, maxWidth: 780 }}>{project.title || project.desc}</span>
            </div>
            <div style={{ display: 'flex', flex: '0 0 auto', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end' }}>
              <Link
                to="/vaults"
                style={{
                  alignItems: 'center',
                  background: 'var(--frm-workbench-panel-2)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  borderRadius: 9,
                  color: 'var(--frm-accent)',
                  display: 'inline-flex',
                  fontSize: 13,
                  fontWeight: 800,
                  gap: 8,
                  height: 38,
                  padding: '0 14px',
                  textDecoration: 'none',
                }}
              >
                <FolderIcon />
                Папка проекта
              </Link>
              <Button variant="secondary" size="sm" leftIcon={<FileIcon />} onClick={() => setContextOpen(true)}>
                context.md
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<ChatIcon />}
                loading={openChatMutation.isPending}
                onClick={() => openChatMutation.mutate(undefined)}
              >
                Открыть в чате с агентом
              </Button>
            </div>
          </div>

          <div className="projects-meta-grid" style={{ ...META_GRID_STYLE, marginTop: 18 }}>
            <MetaCard label="Активный спринт" value={project.sprint?.id ?? '—'} tone={project.sprint ? 'info' : 'neutral'} />
            <MetaCard label="Прогресс спринта" value={project.sprint ? `${project.sprint.done} / ${project.sprint.total} задач` : '—'} />
            <MetaCard label="Vault · знания" value={project.ahead.vault > 0 ? `+${project.ahead.vault} к выпуску` : 'синхронизирован'} tone={project.ahead.vault > 0 ? 'info' : 'success'} />
            <MetaCard label="Последнее обновление" value={formatUpdated(project.lastUpdate)} />
          </div>
        </WorkbenchPanel>

        <MetricStrip
          minColumnWidth={160}
          density="compact"
          cardLayout="row"
          items={[
            { id: 'ahead', label: 'невыпущено', value: `+${formatInteger(project.commits.length)}`, tone: project.commits.length > 0 ? 'warning' : 'neutral' },
            { id: 'sprints', label: 'спринтов', value: formatInteger(project.sprintHistory.length) },
            { id: 'tasks', label: 'открытых задач', value: formatInteger(openTasks), tone: openTasks > 0 ? 'info' : 'neutral' },
            { id: 'agent', label: 'агент чата', value: project.chatAgent },
          ]}
        />

        <ContextAssembly project={project} preview={previewQuery.data} />

        <div className="projects-detail-grid" style={GRID_STYLE}>
          <div style={STACK_STYLE}>
            <WorkbenchPanel
              title="Концепция"
              description="context.md · ## Концепция"
              actions={contextQuery.isFetching ? <Pill tone="info">обновление</Pill> : <Pill tone="neutral">{contextQuery.data?.path ?? `${project.id}/context.md`}</Pill>}
            >
              {contextQuery.isError ? (
                <ErrorBanner
                  title="Не удалось загрузить context.md"
                  description={contextQuery.error instanceof Error ? contextQuery.error.message : 'unknown error'}
                  action={<Button onClick={() => void contextQuery.refetch()}>Повторить</Button>}
                />
              ) : (
                <ConceptBlocks blocks={concept} />
              )}
            </WorkbenchPanel>
            <CommitQueue project={project} />
          </div>
          <div style={STACK_STYLE}>
            <ActiveSprintCard project={project} />
            <ProjectSessionsWidget
              project={project}
              openingChat={openChatMutation.isPending}
              onOpenChat={(input) => openChatMutation.mutate(input)}
            />
          </div>
        </div>

        <div className="projects-sprint-task-grid" style={SPRINT_TASK_GRID_STYLE}>
          <SprintList sprints={sprintItems} selectedSprintId={selectedSprint?.id ?? null} onSelect={setSelectedSprintId} />
          <SprintTasks
            project={project}
            sprint={selectedSprint}
            openingChat={openChatMutation.isPending}
            onOpenChat={(input) => openChatMutation.mutate(input)}
          />
        </div>
      </div>

      <Modal
        open={contextOpen}
        onClose={() => setContextOpen(false)}
        title={`${project.name} · context.md`}
        description={contextQuery.data?.path ?? project.id}
        size="lg"
        closeLabel="Закрыть"
      >
        {contextQuery.isLoading ? (
          <Skeleton height={360} />
        ) : contextQuery.isError ? (
          <ErrorBanner
            title="Не удалось открыть context.md"
            description={contextQuery.error instanceof Error ? contextQuery.error.message : 'unknown error'}
            action={<Button onClick={() => void contextQuery.refetch()}>Повторить</Button>}
          />
        ) : (
          <Markdown source={contextQuery.data?.content ?? ''} />
        )}
      </Modal>
    </>
  )
}

function ChevronIcon({ open = false, back = false }: { open?: boolean; back?: boolean }) {
  return <FrmIcon name="chevron" size={14} style={{ transform: back ? 'rotate(90deg)' : open ? undefined : 'rotate(-90deg)', transition: 'transform 150ms ease' }} />
}

function ChatIcon() {
  return (
    <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function FileIcon() {
  return (
    <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  )
}

function FolderIcon() {
  return (
    <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  )
}

function ListIcon() {
  return (
    <svg aria-hidden width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
      <path d="M3 6h13M3 12h9M3 18h13" />
      <path d="m17 8 4 4-4 4" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg aria-hidden width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  )
}
